/**
 * The static state palette: the ONE color family in the form renderer that is
 * deliberately NOT accent-derived and has no slot in the form-theme vocabulary.
 *
 * Outcome semantics are regulatory, so a red-accented utility must never render
 * a passing test in its brand red and a green-accented one must never render a
 * failure in green. Every slot in the form-theme vocabulary derives from the org
 * accent, so these tones have nowhere to resolve from and are fixed literals by
 * design.
 *
 * It lives here rather than in either renderer for the same reason
 * `SECTION_ICON_PATHS` does: both platforms paint the SAME outcome in the same
 * color, and two copies of a hex have no mechanism to stay equal — only a
 * convention that someone edits both. That convention already failed once: the
 * web pass/fail pills drifted onto a general-purpose utility palette while the
 * mobile ones carried these values, so the same passing test rendered in two
 * different greens depending on which device the tester held.
 *
 * A color string is plain data that a Tailwind arbitrary value and a React
 * Native style object both accept verbatim, so keeping it here costs this
 * package no dependency and neither app any coupling to the other.
 *
 * ## Where the values come from
 *
 * The mockups under `packages/form-logic/mockups/` are the binding visual
 * contract, and every tone below is read off them:
 *
 * | Value | Mockup source |
 * | --- | --- |
 * | `POSITIVE.solid` `#12b76a` | `.fa-pf .sel` edge, `.fa-d.done` fill |
 * | `POSITIVE.tint` `#ecfdf3` | `.fa-pf .sel` fill, `.fa-out` fill |
 * | `POSITIVE.softEdge` `#d1fadf` | `.fa-out` edge |
 * | `POSITIVE.ink.light` `#027a48` | `.fa-pf .sel` ink, `.fa-out .v` ink |
 * | `POSITIVE.ink.dark` `#4ade80` | `.fd-pf .sel` ink, `.fd-out .v` ink |
 * | `NEGATIVE.solid` `#f04438` | `.fa-f label .req`, `.fd-errf` edge base |
 * | `NEGATIVE.ink.light` `#b42318` | `.fa-errmsg`, `.fa-ri.err` |
 * | `NEGATIVE.ink.dark` `#f98080` | `.fd-errmsg`, `.fd-ri.err` |
 *
 * `NEGATIVE.tint` is the one value NOT transcribed from a mockup, because no
 * mockup depicts a selected FAIL pill or any other large danger wash. It is the
 * danger ramp's step that structurally matches the depicted `POSITIVE.tint`, so
 * the two pills read at one weight. The mockups' only light danger fill is
 * `.fa-d.errd`'s `#fee4e2`, and that is deliberately NOT reused: it fills a
 * 16px rail dot, whose positive twin `.fa-d.done` is SOLID `#12b76a` rather than
 * a wash — a dot's fill weight says nothing about a pill's.
 *
 * WEIGHTS BELONG TO A SURFACE, NOT TO A TONE. The dark-mode alphas each surface
 * composites `solid` at are deliberate emphasis: a selected pill is a committed
 * answer, an outcome chip is borderless so its fill alone must read as a chip,
 * a tip is advice. Only the pass/fail pill's weights are published here, because
 * the pill is ONE surface rendered on two platforms and its weights are
 * therefore cross-platform data. The chip and tip weights stay beside the code
 * that applies them.
 */

/** A fixed outcome tone. `tint` is the light-mode fill; dark-mode fills and
 * edges composite `solid` at a per-surface alpha. */
export interface StateTone {
  /** Full-strength tone: the light-mode hard edge, and the base every dark-mode
   * fill/edge alpha is taken from. */
  readonly solid: string;
  /** The light-mode fill. */
  readonly tint: string;
  /** Type color on the tone's own fill, per resolved mode. */
  readonly ink: { readonly light: string; readonly dark: string };
}

/** The positive tone additionally publishes a quiet light-mode edge, for the
 * surfaces that want a hairline rather than the full-strength rule. */
export interface PositiveStateTone extends StateTone {
  readonly softEdge: string;
}

export const POSITIVE: PositiveStateTone = {
  solid: "#12b76a",
  tint: "#ecfdf3",
  softEdge: "#d1fadf",
  ink: { light: "#027a48", dark: "#4ade80" },
};

export const NEGATIVE: StateTone = {
  solid: "#f04438",
  tint: "#fef3f2",
  ink: { light: "#b42318", dark: "#f98080" },
};

/**
 * `#rrggbb` at an alpha, as the `rgba()` string both a Tailwind arbitrary value
 * and a React Native style object accept. Package-internal: callers read the
 * composed treatments below rather than re-deriving a weight, which is the
 * whole point of publishing them.
 */
function withAlpha(hex: string, alpha: number): string {
  const body = /^#([0-9a-f]{6})$/i.exec(hex)?.[1];
  if (body === undefined) return hex;
  const n = parseInt(body, 16);
  return `rgba(${(n >> 16) & 0xff},${(n >> 8) & 0xff},${n & 0xff},${alpha})`;
}

/** One selected pass/fail pill's three painted slots, in one resolved mode. */
export interface PillTint {
  readonly fill: string;
  readonly edge: string;
  readonly ink: string;
}

/**
 * The pill is the LOUDEST of the static-state surfaces — a selected pill is a
 * committed answer rather than guidance — so it takes a full-strength edge in
 * light and the heaviest dark-mode alphas. Both alphas are read off the dark
 * mockup's `.fd-pf .sel`, and the edge alpha is corroborated by `.fd-errf`,
 * whose danger edge carries the same 0.5.
 */
const PILL_DARK_FILL_ALPHA = 0.12;
const PILL_DARK_EDGE_ALPHA = 0.5;

function pillTreatment(tone: StateTone): Record<"light" | "dark", PillTint> {
  return {
    light: { fill: tone.tint, edge: tone.solid, ink: tone.ink.light },
    dark: {
      fill: withAlpha(tone.solid, PILL_DARK_FILL_ALPHA),
      edge: withAlpha(tone.solid, PILL_DARK_EDGE_ALPHA),
      ink: tone.ink.dark,
    },
  };
}

/**
 * The selected pass/fail pill treatment, per outcome per resolved mode — the
 * cross-platform contract for a surface both renderers draw.
 *
 * Mobile reads these strings straight into its RN style objects. Web cannot:
 * Tailwind extracts utilities by scanning source text, so a class name built
 * from a variable emits no CSS at all. The web pill therefore spells its
 * utilities as literals and pins every one of them against this record by test,
 * which keeps the two platforms provably equal without asking Tailwind to see
 * through an interpolation.
 */
export const PASS_FAIL_PILL: Record<"pass" | "fail", Record<"light" | "dark", PillTint>> = {
  pass: pillTreatment(POSITIVE),
  fail: pillTreatment(NEGATIVE),
};

/** The alpha and geometry of the selected pill's glow, from `.fa-pf .sel`. */
const PILL_SHADOW_ALPHA = 0.2;
const PILL_SHADOW_GEOMETRY = "0 1px 6px";

/**
 * The selected pass/fail pill's drop glow — ONE value per outcome, the same in
 * BOTH modes.
 *
 * A CSS shadow string rather than a per-mode record, because this is the one
 * surface where the two platforms had THREE behaviours between them: web drew
 * the light mockup's `0 1px 6px …0.2` in both modes, the dark mockup specifies
 * `0 1px 10px …0.18`, and mobile drew no shadow at all. One surface cannot have
 * three renderings, so it is decided here and read from here by both.
 *
 * THE DECISION IS WEB'S EXISTING BEHAVIOUR, deliberately. Web already treats the
 * light value as mode-invariant on the record ("the shadow is the mockup's light
 * `0 1px 6px` in both modes already"), and print stylesheets drop shadows so
 * that choice carries no print hazard. The dark mockup's wider, slightly softer
 * variant is therefore a recorded deviation, not an oversight: adopting it would
 * change what web renders in dark mode, which is a re-baselining decision rather
 * than a reconciliation. Mobile now mirrors web instead of drawing nothing.
 *
 * Mobile consumes the string verbatim through React Native's CSS-shaped
 * `boxShadow` — the same style the control kit's focus ring already rides.
 * Web cannot: Tailwind extracts utilities by scanning source TEXT, so its pill
 * spells the literal and pins it against this value by test, exactly as it does
 * for the fill, edge and ink above.
 */
export const PASS_FAIL_PILL_SHADOW: Record<"pass" | "fail", string> = {
  pass: `${PILL_SHADOW_GEOMETRY} ${withAlpha(POSITIVE.solid, PILL_SHADOW_ALPHA)}`,
  fail: `${PILL_SHADOW_GEOMETRY} ${withAlpha(NEGATIVE.solid, PILL_SHADOW_ALPHA)}`,
};
